import { Dtos, proConId, type SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { useRequest } from "../../../utils/request";
import { proConsQueryKey } from "../decisioning.keys";
import { type MutationFailureAction } from "../mutationFailure";
import { replaceById } from "../optimistic";
import { bucketSize } from "../rules";
import { type Factor, type ProCon } from "../types";
import { useOptimisticListMutation } from "../useOptimisticListMutation";

export type CreateProConInput = Dtos.Decisioning.ProCon.Create.Request & {
	/** Catalog element the new pro/con embeds optimistically; the server holds the source of truth. */
	factor: Factor;
};

type UseCreateProConOptions = {
	/** Runs with the server copy once it replaces the optimistic one, even if the caller has unmounted. */
	onCreated?: (created: ProCon) => void;
	/** Offered with the failure toast, e.g. reopening the sheet with what was typed. */
	failureAction?: (input: CreateProConInput) => MutationFailureAction;
};

export const useCreateProCon = (
	accountId: SuretyAccountId,
	{ onCreated, failureAction }: UseCreateProConOptions = {},
) => {
	const { post } = useRequest();

	const mutation = useOptimisticListMutation<ProCon, CreateProConInput, ProCon>({
		queryKey: proConsQueryKey(accountId),
		request: async ({ factor: _factor, ...request }) =>
			Dtos.Decisioning.ProCon.Get.Response.schema.parse(
				await post<unknown>(`/v2/admin/surety/accounts/${accountId}/pro-cons`, request),
			),
		apply: (items, input, pendingId) => {
			const now = DateTime.now();
			return [
				...items,
				{
					id: proConId.parse(pendingId),
					accountId,
					kind: input.kind,
					factor: input.factor,
					detail: input.detail,
					observedValue: input.observedValue ?? null,
					observedValueSource: Option.isSome(input.observedValue) ? "underwriter" : null,
					importance: input.importance,
					rank: bucketSize(items, input.kind, input.importance),
					context: "",
					retiredAt: null,
					retiredByUserId: null,
					createdAt: now,
					updatedAt: now,
				},
			];
		},
		affectedIds: (_input, pendingId) => [pendingId],
		failureMessage: (input) => `Couldn't add ${input.factor.name}.`,
		failureAction,
		creates: true,
		reconcile: (items, created, _input, pendingId) => replaceById(items, pendingId, created),
		onSuccess: (created) => onCreated?.(created),
	});

	return {
		createProCon: mutation.mutate,
		/** Resolves once the server confirms, so a confirmation can wait for it; rejects on failure. */
		createProConAsync: mutation.mutateAsync,
	};
};
